import Tooltip from '@apps/components/Tooltip';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HelpIcon from '@mui/icons-material/Help';
import { Button, Stack, Typography } from '@mui/material';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import Image, { StaticImageData } from 'next/image';

export type CarProfileProps = {
  carModel: string;
  carBrandImage: string;
  carImage?: string | StaticImageData;
  lowestPrice?: string;
  highestPrice?: string;
  carBrochure?: string;
  carPricelist?: string;
  carBrand?: string;
  carTitle?: string;
  estPriceWithTax?: {
    min?: number;
    max?: number;
  };
};

/**
 *
 * ===========================
 * MAIN
 * ===========================
 */
export const CarProfile: React.FC<CarProfileProps> = (props) => {
  const {
    carModel,
    carBrandImage,
    carImage,
    lowestPrice,
    highestPrice,
    estPriceWithTax,
    carPricelist,
    carBrochure,
    carTitle,
    carBrand,
  } = props;

  const { sm, md } = useGetResponsive();
  const isTBC =
    parseFloat(lowestPrice || '0') === 0 &&
    parseFloat(highestPrice || '0') === 0;

  const handleDownload = (url: string, fileName: string = 'file.pdf') => {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        console.error('Error fetching the file:', error);
      });
  };

  const text =
    'Prices mentioned is OTR without insurance and with sales tax exemption. Price may vary get quote now to obtain our latest offer.';

  // ======================= VARIABLES

  // ======================= VIEWS
  const renderPrice = () => {
    if (isTBC)
      return (
        <Typography fontWeight={700} fontSize={'1.25rem'} color="#1E242F">
          TBC
        </Typography>
      );
    if (
      lowestPrice &&
      highestPrice &&
      Number(lowestPrice) === Number(highestPrice)
    )
      return (
        <>
          <Typography fontWeight={400} fontSize="0.875rem" color="#1E242F">
            From RM
          </Typography>
          <Typography fontWeight={700} fontSize={'1.25rem'} color="#1E242F">
            {Math.floor(parseFloat(lowestPrice || '0'))?.toLocaleString()}
          </Typography>
          <Tooltip title={text}>
            <HelpIcon sx={{ color: '#737CBC', height: 18, width: 18 }} />
          </Tooltip>
        </>
      );
    return (
      <>
        <Typography fontWeight={400} fontSize="0.875rem" color="#1E242F">
          From RM
        </Typography>
        <Typography fontWeight={700} fontSize={'1.25rem'} color="#1E242F">
          {Math.floor(parseFloat(lowestPrice || '0'))?.toLocaleString()} -{' '}
          {Math.floor(parseFloat(highestPrice || '0'))?.toLocaleString()}
        </Typography>
        <Tooltip title={text}>
          <HelpIcon sx={{ color: '#737CBC', height: 18, width: 18 }} />
        </Tooltip>
      </>
    );
  };

  const renderEstPriceWithTax = () => {
    if (estPriceWithTax === null) return <></>;

    if (
      (estPriceWithTax?.min ?? 0 > 0) &&
      estPriceWithTax?.min == estPriceWithTax?.max
    )
      return (
        <>
          <Typography fontWeight={400} fontSize="0.875rem" color="#1E242F">
            EV price in 2026 without Tax Exemptions:
            {/* If same amount, show once */}
          </Typography>
          <Typography fontWeight={700} fontSize="0.875rem">
            RM{' '}
            {estPriceWithTax?.min == estPriceWithTax?.max
              ? Math.floor(estPriceWithTax?.min ?? 0)?.toLocaleString()
              : ''}
          </Typography>
        </>
      );

    return (
      <Typography fontWeight={400} fontSize="0.875rem" color="#1E242F">
        EV price in 2026 without Tax Exemptions:
        <Typography fontWeight={700} fontSize="0.875rem">
          RM {Math.floor(estPriceWithTax?.min ?? 0)?.toLocaleString()} - RM{' '}
          {Math.floor(estPriceWithTax?.max ?? 0)?.toLocaleString()}
        </Typography>
      </Typography>
    );
  };
  return (
    <Stack>
      <Stack flexDirection="row" alignItems={'center'} gap={3}>
        <Image
          src={carBrandImage}
          alt={carBrand || 'car brand'}
          width={'0'}
          height={'0'}
          sizes="100vw"
          loading={'eager'}
          style={{
            height: '100%',
            width: '100%',
            maxWidth: sm ? '32px' : '42px',
            alignSelf: 'center',
          }}
        />
        <Typography
          fontWeight={700}
          fontSize={{
            xs: '1.5rem',
            md: '2.25rem',
          }}
          color="#1E242F"
          variant="h1"
        >
          {carModel}
        </Typography>
      </Stack>

      <Stack flexDirection="row" alignItems={'center'} gap={2}>
        {renderPrice()}
      </Stack>
      <Stack flexDirection="row" alignItems={'center'} gap={1}>
        {renderEstPriceWithTax()}
      </Stack>

      <Stack flexDirection="row" alignItems={'center'} gap={3} pt={4}>
        {carPricelist && (
          <Button
            startIcon={
              <FileDownloadOutlinedIcon
                style={{
                  color: '#374255',
                  height: md ? 16 : 24,
                  width: md ? 16 : 24,
                }}
              />
            }
            variant="outlined"
            sx={{
              padding: '7px 16px',
              borderColor: '#EDECED',
              borderRadius: '3px',
              fontSize: {
                xs: '0.875rem',
                md: '1rem',
              },
              lineHeight: {
                xs: '1.25rem !important',
                md: '1.375rem !important',
              },
              fontWeight: 500,
              color: '#1E242F',
              textTransform: 'capitalize',
            }}
            onClick={() => {
              handleDownload(carPricelist || '', 'pricelist.pdf');
            }}
          >
            Price List
          </Button>
        )}
        {carBrochure && (
          <Button
            startIcon={
              <FileDownloadOutlinedIcon
                style={{
                  color: '#374255',
                  height: md ? 16 : 24,
                  width: md ? 16 : 24,
                }}
              />
            }
            variant="outlined"
            sx={{
              padding: '7px 16px',
              borderColor: '#EDECED',
              borderRadius: '3px',
              fontSize: {
                xs: '0.875rem',
                md: '1rem',
              },
              lineHeight: {
                xs: '1.25rem !important',
                md: '1.375rem !important',
              },
              fontWeight: 500,
              color: '#1E242F',
              textTransform: 'capitalize',
            }}
            onClick={() => {
              handleDownload(carBrochure || '', 'brochure.pdf');
            }}
          >
            Brochure
          </Button>
        )}
      </Stack>

      <Image
        src={carImage as StaticImageData}
        alt={carTitle || 'Car'}
        width={'0'}
        height={'0'}
        loading={'eager'}
        sizes="100vw"
        style={{
          height: '100%',
          width: '100%',
          maxWidth: '95%',
          alignSelf: 'center',
        }}
      />
    </Stack>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default CarProfile;
